import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/UserManagement.css';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { User, Company } from '../types/auth';
import { getAvatarUrl } from '../utils/avatarUtils';
import { useDataRefresh } from '../contexts/DataRefreshContext';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../utils/dateUtils';

interface NewUser {
  username: string;
  email: string;
  personalNumber: string;
  companyId: number | null;
  userType: number;
}

interface EditUser {
  id: number;
  username: string;
  email: string;
  personalNumber: string;
  companyId: number | null;
  userType: number;
}

type SortField = 'username' | 'email' | 'userType' | 'companyId' | 'isActive' | 'isVerified' | 'lastUpdatedDate';
type SortDirection = 'asc' | 'desc';

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { refreshAllData } = useDataRefresh();
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState<number | null>(null);
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    personalNumber: '',
    companyId: null,
    userType: 2 // Default to company user
  });
  const [editUser, setEditUser] = useState<EditUser | null>(null);
  const usersPerPage = 10;

  // Check if current user is admin
  const isAdmin = () => {
    const userData = authStorage.getUserData();
    return userData?.companyId === null && userData?.userType === 1;
  };

  useEffect(() => {
    loadUsersAndCompanies();
  }, []);

  const loadUsersAndCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }
      
      // Load users and companies in parallel
      const [usersResponse, companiesResponse] = await Promise.all([
        authApi.getAllUsers(token),
        authApi.getAllCompanies(token)
      ]);
      
      setUsers(usersResponse);
      setCompanies(companiesResponse);
      
      // Reset to first page when data changes
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term and company filter
  const getFilteredUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search) ||
        (user.personalNumber && user.personalNumber.toLowerCase().includes(search))
      );
    }

    // Apply company filter
    if (selectedCompanyFilter !== null) {
      filtered = filtered.filter(user => user.companyId === selectedCompanyFilter);
    }

    return filtered;
  };

  const filteredUsers = getFilteredUsers();
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Ensure current page is valid for filtered results
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const sortUsers = (users: User[]) => {
    return [...users].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      // Handle company name sorting
      if (sortField === 'companyId') {
        aValue = getCompanyName(a.companyId);
        bValue = getCompanyName(b.companyId);
      }
      
      // Handle date sorting
      if (sortField === 'lastUpdatedDate') {
        aValue = new Date(a.lastUpdatedDate).getTime();
        bValue = new Date(b.lastUpdatedDate).getTime();
      }
      
      // Handle user type sorting
      if (sortField === 'userType') {
        aValue = getUserTypeName(a.userType);
        bValue = getUserTypeName(b.userType);
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getCurrentPageUsers = () => {
    const sortedUsers = sortUsers(filteredUsers);
    const startIndex = (currentPage - 1) * usersPerPage;
    return sortedUsers.slice(startIndex, startIndex + usersPerPage);
  };

  const handleEditUser = (user: User) => {
    setEditUser({
      id: user.id,
      username: user.username,
      email: user.email,
      personalNumber: user.personalNumber || '',
      companyId: user.companyId,
      userType: user.userType
    });
    setShowEditUserModal(true);
  };

  const handleViewUser = (userId: number) => {
    console.log('View user:', userId);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!isAdmin()) {
      showError(t('notifications.error.unauthorized'));
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        showError(t('notifications.error.unauthorized'));
        return;
      }

      await authApi.deleteUser(token, userId);
      
      // Reload users to reflect the deletion
      await loadUsersAndCompanies();
      
      // Show success notification
      showSuccess(t('notifications.success.userDeleted'));
      
      // Refresh AdminViewPage data if context is available
      if (refreshAllData) {
        await refreshAllData();
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      if (error instanceof ApiError) {
        showError(error.message);
      } else {
        showError(t('notifications.error.userDeleteFailed'));
      }
    }
  };

  const handleAddUser = () => {
    setShowAddUserModal(true);
  };

  const handleExportUsers = async () => {
    if (!isAdmin()) {
      showError(t('notifications.error.unauthorized'));
      return;
    }

    try {
      setExporting(true);
      const token = authStorage.getAccessToken();
      if (!token) {
        showError(t('notifications.error.unauthorized'));
        return;
      }

      const blob = await authApi.exportActiveUsersCsv(token);

      // Trigger a browser download of the returned CSV file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'active-users.csv';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(t('common.exportUsersSuccess', 'Users exported successfully.'));
    } catch (error) {
      console.error('Failed to export users:', error);
      if (error instanceof ApiError) {
        showError(error.message);
      } else {
        showError(t('common.exportUsersFailed', 'Failed to export users. Please try again.'));
      }
    } finally {
      setExporting(false);
    }
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setNewUser({
      username: '',
      email: '',
      personalNumber: '',
      companyId: null,
      userType: 2
    });
  };

  const handleCloseEditUserModal = () => {
    setShowEditUserModal(false);
    setEditUser(null);
  };

  const handleSaveEditUser = async () => {
    if (!editUser || !isAdmin()) {
      showError(t('notifications.error.unauthorized'));
      return;
    }

    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        showError(t('notifications.error.unauthorized'));
        return;
      }

      // Update user with API
      await authApi.updateUser(token, editUser.id, {
        username: editUser.username,
        personalNumber: editUser.personalNumber || null,
        email: editUser.email,
        userType: editUser.userType,
        companyId: editUser.companyId
      });

      // Reload users to show the updated user
      await loadUsersAndCompanies();
      handleCloseEditUserModal();
      
      // Show success notification
      showSuccess(t('notifications.success.userUpdated'));
      
      // Refresh AdminViewPage data if context is available
      if (refreshAllData) {
        await refreshAllData();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      if (error instanceof ApiError) {
        showError(error.message);
      } else {
        showError(t('notifications.error.userUpdateFailed'));
      }
    }
  };

  const handleSaveUser = async () => {
    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Create user with API
      await authApi.createUserWithPassword(token, {
        profileImageUrl: null, // Will use dicebear avatar based on username
        username: newUser.username,
        personalNumber: newUser.personalNumber || null,
        email: newUser.email,
        userType: newUser.userType,
        companyId: newUser.companyId
      });

      // Reload users to show the new user
      await loadUsersAndCompanies();
      handleCloseAddUserModal();
      
      // Refresh AdminViewPage data if context is available
      if (refreshAllData) {
        await refreshAllData();
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to create user. Please try again.');
      }
    }
  };

  const handleInputChange = (field: keyof NewUser, value: string | number | null) => {
    setNewUser(prev => ({ ...prev, [field]: value }));
  };

  const handleEditInputChange = (field: keyof EditUser, value: string | number | null) => {
    if (!editUser) return;
    setEditUser(prev => prev ? ({ ...prev, [field]: value }) : null);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleCompanyFilterChange = (companyId: number | null) => {
    setSelectedCompanyFilter(companyId);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCompanyFilter(null);
    setCurrentPage(1);
  };

  const getUserTypeName = (userType: number) => {
    return userType === 1 ? 'Internal User' : 'Company User';
  };

  const getCompanyName = (companyId: number | null) => {
    if (!companyId) return 'N/A';
    const company = companies.find(c => c.id === companyId);
    return company ? company.companyName : 'Unknown Company';
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading">
          {t('common.loading', 'Loading users...')}
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>{t('navigation.users', 'Users')}</h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Search, Filter, and Add User Controls Row */}
      <div className="controls-row">
        <div className="controls-left">
          <div className="search-section">
            <div className="search-bar">
              <input
                type="text"
                placeholder={String(t('common.searchUsers', 'Search users by name, email, or personal number...'))}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="search-input"
                disabled={loading}
              />
              {(searchTerm || selectedCompanyFilter !== null) && (
                <button
                  onClick={clearFilters}
                  className="clear-filters-btn"
                  title={String(t('common.clearFilters', 'Clear filters'))}
                  disabled={loading}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {isAdmin() && (
            <div className="filter-section">
              <select
                value={selectedCompanyFilter || ''}
                onChange={(e) => handleCompanyFilterChange(e.target.value ? parseInt(e.target.value) : null)}
                className="filter-select"
                disabled={loading || companies.length === 0}
              >
                <option value="">{t('common.allCompanies', 'All Companies')}</option>
                {[...companies]
                  .sort((a, b) => a.companyName.localeCompare(b.companyName))
                  .map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.companyName}
                    </option>
                  ))}
              </select>
              {companies.length === 0 && !loading && (
                <span className="filter-hint">{t('common.noCompaniesAvailable', 'No companies available')}</span>
              )}
            </div>
          )}
        </div>

        <div className="add-user-section">
          {isAdmin() && (
            <button
              className="export-users-button"
              onClick={handleExportUsers}
              disabled={exporting}
              title={String(t('common.exportUsersTitle', 'Export active users as CSV'))}
            >
              {exporting
                ? t('common.exportingUsers', 'Exporting...')
                : t('common.exportUsers', 'Export Users')}
            </button>
          )}
          <button className="add-user-button" onClick={handleAddUser}>
            {t('navigation.addUser', 'Add User')}
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="results-summary">
        {searchTerm || selectedCompanyFilter !== null ? (
          <p>{t('common.showingResults', 'Showing {{count}} of {{total}} users', {
            count: filteredUsers.length,
            total: users.length
          })}</p>
        ) : (
          <p>{t('common.totalUsers', 'Total users: {{count}}', { count: users.length })}</p>
        )}
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('username')}>
                {t('common.username', 'Username')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('email')}>
                {t('common.email', 'Email')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('userType')}>
                {t('common.userType', 'User Type')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('companyId')}>
                {t('common.company', 'Company')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('isActive')}>
                {t('common.status', 'Status')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('isVerified')}>
                {t('common.verified', 'Verified')} <span className="sort-icon">↑</span>
              </th>
              <th className="sortable" onClick={() => handleSort('lastUpdatedDate')}>
                {t('common.updated', 'Last Updated')} <span className="sort-icon">↑</span>
              </th>
              {isAdmin() && (
                <th className="actions-column">
                  {t('common.actions', 'Actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {getCurrentPageUsers().map(user => (
              <tr key={user.id} className="user-row">
                <td className="user-cell">
                  <div className="user-info-cell">
                    <img 
                      src={getAvatarUrl(user.profileImageUrl, user.username)} 
                      alt={user.username}
                      className="user-avatar-small"
                    />
                    <span className="user-name">{user.username}</span>
                  </div>
                </td>
                <td className="user-cell">{user.email}</td>
                <td className="user-cell">
                  <span className="user-type-badge">{getUserTypeName(user.userType)}</span>
                </td>
                <td className="user-cell">{getCompanyName(user.companyId)}</td>
                <td className="user-cell">
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                  </span>
                </td>
                <td className="user-cell">
                  <span className={`verification-badge ${user.isVerified ? 'verified' : 'unverified'}`}>
                    {user.isVerified ? t('common.verified', 'Verified') : t('common.unverified', 'Unverified')}
                  </span>
                </td>
                <td className="user-cell">
                  {formatDate(user.lastUpdatedDate)}
                </td>
                {isAdmin() && (
                  <td className="user-cell">
                    <div className="user-actions">
                      <button 
                        className="action-button edit"
                        onClick={() => handleEditUser(user)}
                        title={String(t('common.edit', 'Edit'))}
                      >
                        ✏️
                      </button>
                      <button 
                        className="action-button delete"
                        onClick={() => handleDeleteUser(user.id)}
                        title={String(t('common.delete', 'Delete'))}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination - only show if there are users and multiple pages */}
      {filteredUsers.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-button"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            «
          </button>
          <button 
            className="pagination-button"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-button ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          
          <button 
            className="pagination-button"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button 
            className="pagination-button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            »
          </button>
        </div>
      )}

      {/* No results message */}
      {filteredUsers.length === 0 && !loading && (
        <div className="no-results">
          <p>{searchTerm || selectedCompanyFilter !== null ? 
            t('common.noResultsFound', 'No users found matching your search criteria.') :
            t('common.noUsersFound', 'No users found.')
          }</p>
          {(searchTerm || selectedCompanyFilter !== null) && (
            <button onClick={clearFilters} className="btn-secondary">
              {t('common.clearFilters', 'Clear filters')}
            </button>
          )}
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal-overlay" onClick={handleCloseAddUserModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.addUser', 'Add New User')}</h2>
              <button className="modal-close" onClick={handleCloseAddUserModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="add-user-form">
                <div className="form-group">
                  <label htmlFor="userName">{t('common.username', 'Username')}</label>
                  <input
                    type="text"
                    id="userName"
                    value={newUser.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder={String(t('common.enterUsername', 'Enter username'))}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="userEmail">{t('common.email', 'Email')}</label>
                  <input
                    type="email"
                    id="userEmail"
                    value={newUser.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={String(t('common.enterEmail', 'Enter email address'))}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="userPersonalNumber">{t('common.personalNumber', 'Personal Number')}</label>
                  <input
                    type="text"
                    id="userPersonalNumber"
                    value={newUser.personalNumber}
                    onChange={(e) => handleInputChange('personalNumber', e.target.value)}
                    placeholder={String(t('common.enterPersonalNumber', 'Enter personal number (optional)'))}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="userType">{t('common.userType', 'User Type')}</label>
                  <select
                    id="userType"
                    value={newUser.userType}
                    onChange={(e) => handleInputChange('userType', parseInt(e.target.value))}
                  >
                    <option value={2}>{t('common.companyUser', 'Company User')}</option>
                    <option value={1}>{t('common.internalUser', 'Internal User')}</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="userCompany">{t('common.company', 'Company')}</label>
                  <select
                    id="userCompany"
                    value={newUser.companyId || ''}
                    onChange={(e) => handleInputChange('companyId', e.target.value ? parseInt(e.target.value) : null)}
                    disabled={newUser.userType === 1}
                  >
                    <option value="">{t('common.selectCompany', 'Select a company')}</option>
                    {[...companies]
                      .sort((a, b) => a.companyName.localeCompare(b.companyName))
                      .map((company) => (
                        <option key={company.id} value={company.id}>{company.companyName}</option>
                      ))}
                  </select>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseAddUserModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveUser}
                disabled={!newUser.username || !newUser.email || (newUser.userType === 2 && !newUser.companyId)}
              >
                {t('common.save', 'Save User')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editUser && isAdmin() && (
        <div className="modal-overlay" onClick={handleCloseEditUserModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.editUser', 'Edit User')}</h2>
              <button className="modal-close" onClick={handleCloseEditUserModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="add-user-form">
                <div className="form-group">
                  <label htmlFor="editUserName">{t('common.username', 'Username')}</label>
                  <input
                    type="text"
                    id="editUserName"
                    value={editUser.username}
                    onChange={(e) => handleEditInputChange('username', e.target.value)}
                    placeholder={String(t('common.enterUsername', 'Enter username'))}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="editUserEmail">{t('common.email', 'Email')}</label>
                  <input
                    type="email"
                    id="editUserEmail"
                    value={editUser.email}
                    onChange={(e) => handleEditInputChange('email', e.target.value)}
                    placeholder={String(t('common.enterEmail', 'Enter email address'))}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="editUserPersonalNumber">{t('common.personalNumber', 'Personal Number')}</label>
                  <input
                    type="text"
                    id="editUserPersonalNumber"
                    value={editUser.personalNumber}
                    onChange={(e) => handleEditInputChange('personalNumber', e.target.value)}
                    placeholder={String(t('common.enterPersonalNumber', 'Enter personal number (optional)'))}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="editUserType">{t('common.userType', 'User Type')}</label>
                  <select
                    id="editUserType"
                    value={editUser.userType}
                    onChange={(e) => handleEditInputChange('userType', parseInt(e.target.value))}
                  >
                    <option value={2}>{t('common.companyUser', 'Company User')}</option>
                    <option value={1}>{t('common.internalUser', 'Internal User')}</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="editUserCompany">{t('common.company', 'Company')}</label>
                  <select
                    id="editUserCompany"
                    value={editUser.companyId || ''}
                    onChange={(e) => handleEditInputChange('companyId', e.target.value ? parseInt(e.target.value) : null)}
                    disabled={editUser.userType === 1}
                  >
                    <option value="">{t('common.selectCompany', 'Select a company')}</option>
                    {[...companies]
                      .sort((a, b) => a.companyName.localeCompare(b.companyName))
                      .map((company) => (
                        <option key={company.id} value={company.id}>{company.companyName}</option>
                      ))}
                  </select>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseEditUserModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveEditUser}
                disabled={!editUser.username || !editUser.email || (editUser.userType === 2 && !editUser.companyId)}
              >
                {t('common.save', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;